<script lang="ts" setup>
import {
  computed,
  ref,
  toRefs,
  watchEffect,
} from 'vue';

import type { FileResultDTO } from '@nora-monorepo/nora-api-client';
import { getFileIcon } from '@nora-monorepo/ui-shared';

interface FileItemProps {
  file: FileResultDTO;
  width: number;
  height: number;
  type?: 'jpg' | 'png';
}

const props = defineProps<FileItemProps>();
const { file, width, height, type } = toRefs(props);

const finalImageUrl = ref<string | undefined>();

function preloadImage() {
  finalImageUrl.value = undefined;

  const img = new Image();
  const url = file.value.urls?.thumbnail?.url || '';
  const imageType = type.value ?? 'jpg';
  const fullUrl = `${url}&height=${height.value}&width=${width.value}&imageType=${imageType}`;

  img.onload = () => {
    finalImageUrl.value = fullUrl;
  };

  img.src = fullUrl;
}

watchEffect(() => {
  preloadImage();
});

const backgroundImage = computed(() => {
  return {
    backgroundImage: `url(${finalImageUrl.value})`,
    width: `${width.value}px`,
    height: `${height.value}px`,
  };
});

const placeholderStyle = computed(() => {
  return {
    width: `${width.value}px`,
    height: `${height.value}px`,
  };
});
</script>

<template>
  <div
    v-if="finalImageUrl"
    class="shrink-0 overflow-hidden bg-no-repeat bg-fit bg-center"
    :style="backgroundImage"
  >
    <slot />
  </div>
  <div
    v-else
    class="shrink-0 overflow-hidden relative"
    :style="placeholderStyle"
  >
    <div
      class="relative w-full h-full flex items-center justify-center text-light-label-secondary dark:text-dark-label-secondary"
    >
      <span
        class="mi md-36"
        :class="getFileIcon(file.mimeType)"
      />
    </div>
    <slot />
  </div>
</template>
